import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/services/supabaseClient";
import type { Tables, InsertTables, UpdateTables } from "@/types/database";

async function fetchPaymentsByQuoteId(quoteId: string) {
  const { data, error } = await supabase
    .from("payments")
    .select("*")
    .eq("quote_id", quoteId)
    .order("created_at", { ascending: true });

  if (error) throw error;
  return data;
}

async function createPayment(payment: InsertTables<"payments">) {
  const { data, error } = await supabase
    .from("payments")
    .insert([payment])
    .select()
    .single();

  if (error) throw error;
  return data;
}

async function updatePayment(id: string, updates: UpdateTables<"payments">) {
  const { data, error } = await supabase
    .from("payments")
    .update(updates)
    .eq("id", id)
    .select()
    .single();

  if (error) throw error;
  return data;
}

async function deletePayment(id: string) {
  const { error } = await supabase.from("payments").delete().eq("id", id);

  if (error) throw error;
}

export function usePayments(quoteId: string | undefined) {
  return useQuery({
    queryKey: ["payments", quoteId],
    queryFn: () => fetchPaymentsByQuoteId(quoteId!),
    enabled: !!quoteId,
  });
}

export function useCreatePayment() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: createPayment,
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["payments", data.quote_id] });
      queryClient.invalidateQueries({ queryKey: ["events"] });
    },
  });
}

export function useUpdatePayment() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      id,
      updates,
    }: {
      id: string;
      updates: UpdateTables<"payments">;
    }) => updatePayment(id, updates),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["payments", data.quote_id] });
      queryClient.invalidateQueries({ queryKey: ["events"] });
    },
  });
}

export function useDeletePayment() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: deletePayment,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["payments"] });
      queryClient.invalidateQueries({ queryKey: ["events"] });
    },
  });
}

export type Payment = Tables<"payments">;
